import { AIRBASE_SLOTS, ASSET_SPECS, BALLISTIC_PER_WAVE, BARRACKS_MITIGATION, BASE_SITING_CAP, BASE_WAVES, DEFENCE_AMMO, DOCTRINE_SPECS, MULTI_WAVE_COMMAND_FLOOR, REARM_BASE, DEVELOPMENT_EFFECTS, CAPABILITY_FLOOR, DESTRUCTION_THRESHOLD, RESILIENCE_EXP, SILO_SLOTS, WARHEAD_SPECS, WAVE_DOCTRINE_BONUS, WEAPON_SPECS, } from './constants.js';
// ---------------------------------------------------------------------------
// Derived health of a city. Almost every system asks one of these questions:
// how much of X is still standing, and what does that cost me?
// ---------------------------------------------------------------------------
/** 0..1 fraction of a given asset type's total HP still standing. */
export function integrityOf(city, type) {
    let hp = 0;
    let max = 0;
    for (const a of city.assets) {
        if (a.type !== type)
            continue;
        hp += Math.max(0, a.hp);
        max += a.maxHp;
    }
    if (max === 0)
        return 0;
    return hp / max;
}
/** 0..1 across every asset in the city. Drives the destruction defeat check. */
export function totalIntegrity(city) {
    let hp = 0;
    let max = 0;
    for (const a of city.assets) {
        hp += Math.max(0, a.hp);
        max += a.maxHp;
    }
    for (const b of city.urban) {
        hp += Math.max(0, b.hp);
        max += b.maxHp;
    }
    return max === 0 ? 0 : hp / max;
}
/**
 * A city's health as every screen shows it: 100% whole, 0% at the line it
 * falls at. Integrity, which the defeat check reads, is still half the
 * structure at that line (DESTRUCTION_THRESHOLD), so shown raw a city at 51%
 * read as half standing when one more hit would finish it.
 */
export function standing(integrity) {
    const span = Math.max(Number.EPSILON, 1 - DESTRUCTION_THRESHOLD);
    return Math.min(1, Math.max(0, (integrity - DESTRUCTION_THRESHOLD) / span));
}
export function militaryIntegrity(city) {
    let hp = 0;
    let max = 0;
    for (const a of city.assets) {
        if (a.cls !== 'military')
            continue;
        hp += Math.max(0, a.hp);
        max += a.maxHp;
    }
    return max === 0 ? 0 : hp / max;
}
export function civilianIntegrity(city) {
    let hp = 0;
    let max = 0;
    for (const a of city.assets) {
        if (a.cls !== 'civilian')
            continue;
        hp += Math.max(0, a.hp);
        max += a.maxHp;
    }
    for (const b of city.urban) {
        hp += Math.max(0, b.hp);
        max += b.maxHp;
    }
    return max === 0 ? 0 : hp / max;
}
export function liveCount(city, type) {
    return city.assets.filter((a) => a.type === type && a.hp > 0 && a.offlineFor === 0).length;
}
/**
 * How well your command network is holding up. Losing the bunker does not just
 * cost you a building: it costs you Action Points and strike accuracy.
 */
export function commandFactor(city) {
    return integrityOf(city, 'command');
}
/** Power keeps the factories and the interceptor radars running. */
export function powerFactor(city) {
    return integrityOf(city, 'power_plant');
}
/**
 * Whether a munition can be fitted with a warhead at all.
 *
 * A recon drone carries a camera where the section would go. Letting one be
 * booked with a thermobaric warhead spent the warhead out of the magazine and
 * flew it over the target to take a photograph — the warhead was consumed and
 * nothing was ever delivered, because a recon drone does no damage by design.
 */
export function carriesWarhead(weapon) {
    return weapon !== 'recon_drone';
}
/** Which pool a weapon draws its launch slots from. */
export function slotPool(weapon) {
    return weapon === 'recon_drone' || weapon === 'strike_drone' ? 'drone' : 'missile';
}
/**
 * Launch slots one munition consumes in a wave: the airframe plus whatever extra
 * handling its warhead demands. A nuclear-tipped ballistic missile costs 13 of a
 * silo pool that tops out at 12, which is the whole point — it cannot be fired
 * at all until the silos are in perfect condition, and it books the entire wave
 * when it is.
 */
export function slotCost(weapon, warhead = 'standard') {
    return WEAPON_SPECS[weapon].slots + (WARHEAD_SPECS[warhead]?.slots ?? 0);
}
/**
 * Launch slots available in a single wave. Drones fly off the airbase, missiles
 * come out of the silos, and a degraded command network cannot run either at
 * full tempo. Flatten the airbase and the drone fleet is grounded however many
 * drones are sitting in the arsenal.
 */
export function launchCapacity(city, opts = {}) {
    const airbase = militaryCapability(city, 'airbase');
    const silos = militaryCapability(city, 'silo');
    const cmd = 0.55 + 0.45 * militaryCapability(city, 'command');
    // An all-out city has every crew in the launch bays: twice the slots in each
    // wave, on the rails it still has. Flatten the airbase and the drones stay
    // on the ground however many hands there are. `opts.doctrine` asks what the
    // capacity would be under another stance: an all-out city still ships to
    // its allies at its ordinary rate.
    const mult = DOCTRINE_SPECS[opts.doctrine ?? city.doctrine].launchMult;
    return {
        drones: Math.max(0, Math.floor(AIRBASE_SLOTS * airbase * cmd * mult)),
        missiles: Math.max(0, Math.floor(SILO_SLOTS * silos * cmd * mult)),
    };
}
/**
 * The most ballistic missiles one wave may carry (BALLISTIC_PER_WAVE). Going
 * all out doubles it as it doubles the launch slots; Infinity when no cap is
 * set, which leaves the slots as the only limit.
 */
export function ballisticsPerWave(city, opts = {}) {
    if (BALLISTIC_PER_WAVE <= 0)
        return Infinity;
    return BALLISTIC_PER_WAVE * DOCTRINE_SPECS[opts.doctrine ?? city.doctrine].launchMult;
}
// ---------------------------------------------------------------------------
// The compounding-damage curve.
//
// Everything a city can *do* runs through this rather than through raw integrity.
// It is deliberately flat near the top and steep near the bottom, so two healthy
// cities trade blows on level terms for a long time, and a city that has actually
// fallen behind loses capability far faster than it is losing buildings. That is
// the chess property: down a piece is survivable, down a rook and a bishop is not.
// ---------------------------------------------------------------------------
export function resilience(integrity) {
    const i = Math.max(0, Math.min(1, integrity));
    return CAPABILITY_FLOOR + (1 - CAPABILITY_FLOOR) * (1 - Math.pow(1 - i, RESILIENCE_EXP));
}
/** The same curve applied to one asset type. */
export function capabilityOf(city, type) {
    return resilience(integrityOf(city, type));
}
/** How much the barracks are still worth: their integrity, on the same curve. */
export function barracksFactor(city) {
    return capabilityOf(city, 'barracks');
}
/**
 * What a damaged command bunker, airbase or silo field can still do, with
 * the barracks' help.
 *
 * Barracks keep a damaged military installation working: crews to clear the
 * rubble, run the rails by hand and man the backup post. They soften partial
 * damage — `C = R(x) + (1 − R(x))·b·BARRACKS_MITIGATION` — and nothing else:
 * a healthy facility gains nothing (C is 1 either way), and a flattened one
 * stays at zero, because no amount of manpower launches a missile from a silo
 * that is not there. So the barracks matter most late in a siege, to a city
 * whose military plant is already hurt, and knocking them out is a way to make
 * the damage already done bite harder.
 */
export function militaryCapability(city, type) {
    const x = integrityOf(city, type);
    if (x <= 0)
        return 0;
    const r = resilience(x);
    return Math.min(1, r + (1 - r) * barracksFactor(city) * BARRACKS_MITIGATION);
}
/**
 * Whether a city can send completed equipment to an ally: its broadcast mast
 * has to be standing and on the air. Receiving needs nothing of the kind.
 */
export function canDispatch(city) {
    return liveCount(city, 'comms') > 0;
}
/**
 * How many rounds a REARM order can move out of the depots. With every depot
 * gone this is zero, and the air defence network simply runs dry — which is the
 * single fastest way to break a city that is still structurally intact.
 */
export function rearmCapacity(city) {
    const depots = capabilityOf(city, 'depot');
    return Math.floor(REARM_BASE * depots);
}
/** Magazine depth for a launcher type in this city. */
export function magazineFor(city, type) {
    const base = DEFENCE_AMMO[type] ?? 0;
    if (base === 0)
        return 0;
    const handling = city.development.find((r) => r.id === 'defence_2')?.done
        ? DEVELOPMENT_EFFECTS.defence_2_magazine
        : 1;
    return Math.round(base * handling);
}
/** Rounds in every live launcher held by the city, sited or in reserve. */
export function totalAmmo(city) {
    let held = 0;
    let capacity = 0;
    for (const d of [...city.defences, ...city.reserve]) {
        if (d.hp <= 0)
            continue;
        held += d.ammo;
        capacity += d.maxAmmo;
    }
    return { held, capacity };
}
/**
 * How many waves this city can split a salvo across. Multi-wave attacks are a
 * command problem before they are a logistics one: with the headquarters wrecked
 * you can still launch everything, you just cannot time it.
 */
export function maxWaves(city, opts = {}) {
    const waived = DOCTRINE_SPECS[opts.doctrine ?? city.doctrine].waivesWaveFloor;
    if (!waived && commandFactor(city) < MULTI_WAVE_COMMAND_FLOOR)
        return 1;
    const doctrine = city.development.find((r) => r.id === 'attack_2')?.done
        ? WAVE_DOCTRINE_BONUS
        : 0;
    return BASE_WAVES + doctrine;
}
/**
 * How many interceptors this city can have sited at once (shelters do not
 * count). Scales with the command network and the radars: bomb either and the
 * belt thins out whether the launchers survived or not.
 *
 * Unless the city is dug in. Of everything a wrecked bunker costs you — action
 * points, launch tempo, accuracy — this is the only piece that is defensive,
 * and it is the piece a turtling city overrides: the crews are already at their
 * posts and have nothing left to be told.
 */
export function sitingCap(city) {
    const cmd = 0.45 + 0.55 * militaryCapability(city, 'command');
    const rdr = 0.6 + 0.4 * resilience(integrityOf(city, 'radar'));
    // Fortress City adds its slots to the base before the damage
    // multipliers, not after, so bombing the plot takes the extra posts off you
    // the same way it takes the original ones.
    const base = BASE_SITING_CAP +
        (city.development.find((r) => r.id === 'defence_2')?.done
            ? DEVELOPMENT_EFFECTS.defence_2_slots
            : 0);
    // Launchers a fallen ally's crews brought with them come with their own
    // posts (FALLEN_DEFENCE_SHARE).
    const inherited = [...city.defences, ...city.reserve].filter((u) => u.bequeathed && u.hp > 0).length;
    const normal = Math.max(3, Math.round(base * cmd * rdr)) + inherited;
    // Dug in, the damage stops counting: the city directs as many launchers as
    // it could at full health. It used to be "everything it owns", which had no
    // ceiling once allies could lend units; the healthy cap is the ceiling a
    // reinforced city is built around (spec D-02).
    if (!DOCTRINE_SPECS[city.doctrine].ignoresCommandDamage)
        return normal;
    return Math.max(normal, base + inherited);
}
/** Total starting HP of the roster, for the destruction threshold. */
export function rosterMaxHp() {
    let total = 0;
    for (const key of Object.keys(ASSET_SPECS)) {
        const s = ASSET_SPECS[key];
        total += s.hp * s.count;
    }
    return total;
}
//# sourceMappingURL=metrics.js.map